'use client';

/**
 * API namespace `orders` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L7700-L7960).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const orders = {
  /**
   * POST /api/market/orders — buyer creates a new order.
   *
   * Validates the request body against `orderCreateSchema` (Task A —
   * Zod) on the backend. The `Idempotency-Key` HTTP header is set
   * explicitly on the request when `idempotencyKey` is passed on
   * the options object (INV-SEC-03 — Redis SETNX 24h TTL — same
   * pattern as `seller-product.controller` reading
   * `req.headers['idempotency-key']`).
   *
   * The backend `escrow.service.createOrder` (Task B) flow:
   *   1. Idempotency check (Redis SETNX).
   *   2. Validate product (approved + physical — Q6).
   *   3. Atomic stock decrement (Q3 — `findOneAndUpdate({$inc: {stock:
   *      -quantity}}, {arrayFilters on variation option})`).
   *   4. Calculate prices (totalPrice = unitPrice × quantity,
   *      shippingCost = 0 if pickup OR free-shipping threshold met,
   *      grandTotal = totalPrice + shippingCost).
   *   5. Validate fulfillment (INV-MKT-21 — seller's
   *      `fulfillmentOptions.{ships,pickup}` must support the chosen
   *      method).
   *   6. Generate order number (`OrderCounter.findOneAndUpdate` →
   *      `MKT-{year}-{seq padded 6}` — anti-gap Q5).
   *   7. withFinancialTransaction (INV-SEC-04):
   *      a. atomicallyDebitCredits(buyerId, grandTotal) — escrow debit.
   *      b. MarketOrder.create (status='pending_seller',
   *         escrowStatus='holding', sellerTimeoutAt = now + 72h).
   *      c. LedgerEntry × 2 (debit user:credits, credit
   *         escrow:holding:{orderNumber}).
   *      d. UnifiedTransaction 'order_created'.
   *   8. Post-commit: Socket.IO `order:created` + email.
   *
   * Authz: `requireAuth` + `requireMarketOrdersEnabled` +
   * `orderCreateLimiter` (10/h) + `requireRecent2FA` (TOTP/wallet
   * 2FA within last 5 min — frontend opens
   * `TwoFactorFinancialDialog` on 403 `2FA_REQUIRED`).
   *
   * Spec ref: SPEC-081 Fase 4 §5.1, §4.1 (createOrder); SPEC-NEGOCIO
   *           §10.5 INV-MKT-09 + §10.6 INV-MKT-21.
   */
  async create(
    data: OrderCreateRequestUnion,
    options?: { idempotencyKey?: string; signal?: AbortSignal },
  ): Promise<OrderResponse> {
    return apiFetch<OrderResponse>('/market/orders', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: options?.idempotencyKey
        ? { 'Idempotency-Key': options.idempotencyKey }
        : undefined,
      signal: options?.signal,
    });
  },
  /**
   * GET /api/market/orders?page=&limit=&status= — list the buyer's own
   * orders (paginated). Returns `OrderListResponse` (`orders` + total
   * + page + limit). The backend clamps `page >= 0` and `limit` to
   * `[1, 100]` (defense in depth). The optional `status` filter
   * accepts any `OrderStatus` value.
   *
   * Authz: self only — backend verifies `order.buyerId === req.user!.sub`.
   *
   * Spec ref: SPEC-081 Fase 4 §5.1, §4.1 (listBuyerOrders).
   */
  async list(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<OrderListResponse> {
    return apiFetch<OrderListResponse>(
      `/market/orders?page=${page}&limit=${limit}`,
      { signal },
    );
  },
  /**
   * GET /api/market/orders/:orderNumber — buyer's view of a single
   * order. Returns `OrderResponse` (all fields — buyer + seller see
   * the same shape in Fase 4; Fase 6 may redact buyer's phone after
   * delivery on the seller-side view).
   *
   * Authz: self only — backend verifies `order.buyerId === req.user!.sub`.
   *
   * Spec ref: SPEC-081 Fase 4 §5.1, §4.1 (getOrderByOrderNumber).
   */
  async get(orderNumber: string, signal?: AbortSignal): Promise<OrderResponse> {
    return apiFetch<OrderResponse>(
      `/market/orders/${encodeURIComponent(orderNumber)}`,
      { signal },
    );
  },
  /**
   * POST /api/market/orders/:orderNumber/confirm — buyer confirms
   * receipt. CAS status 'delivered' → 'completed' + buyerConfirmedAt.
   * Calls `releaseEscrow` internally (30/2/68 payout — INV-MKT-06/07/13).
   *
   * NOT 2FA-gated (credits already in escrow — confirming receipt just
   * releases them; a compromised session can only release the escrow,
   * not steal credits). The buyer can also wait 24h for auto-close
   * (INV-MKT-10) — same payout distribution.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-081 Fase 4 §5.1, §4.1 (confirmReceipt + releaseEscrow);
   *           SPEC-NEGOCIO §10.5 INV-MKT-10 + §10.4 INV-MKT-06/07/13.
   */
  async confirmReceipt(orderNumber: string, signal?: AbortSignal): Promise<OrderResponse> {
    return apiFetch<OrderResponse>(
      `/market/orders/${encodeURIComponent(orderNumber)}/confirm`,
      { method: 'POST', signal },
    );
  },
  // ──────────────────────────────────────────────────────────────────
  // SPEC-081 Fase 5 (Servicios + Digitales — Task C1) — 2 new buyer
  // endpoints on `api.market.orders`: `download` (digital asset delivery
  // via signed URL + JWT — INV-MKT-14) + `cancelService` (buyer-
  // initiated service cancellation with Q1 ENMIENDA 30/20 split).
  //
  // Backend wiring (Task B2 — already merged):
  //   - POST /api/market/orders/:orderNumber/download
  //     → `orderController.downloadDigitalOrder` →
  //       `escrowService.recordDownloadAndRelease(orderNumber, userId, ip)`.
  //     NO 2FA gate (credits already in escrow — download just
  //     releases them). NO rate limiter (re-downloads are normal
  //     behavior; the per-product `digitalConfig.downloadLimit` is the
  //     authoritative cap).
  //   - POST /api/market/orders/:orderNumber/cancel
  //     → `orderCancelLimiter` (5/min/user) → `requireRecent2FA`
  //     (5min 2FA — financial-grade refund OR Q1 ENMIENDA 30/20
  //     retention) → `orderController.cancelServiceOrder` →
  //     `escrowService.cancelServiceOrder(orderNumber, userId, reason?)`.
  //
  // §6 Anti-Invención: response shapes mirror SPEC-081 Fase 5 §5.1
  // verbatim. NO business logic invented.
  //
  // Spec ref: SPEC-081 Fase 5 §5.1 (Buyer order routes — download +
  //           cancel), §4.1 (service cancellation flow + Q1 ENMIENDA
  //           30/20 split), §4.2 (digital download flow + INV-MKT-14
  //           immediate release); SPEC-NEGOCIO §10.7 (servicios —
  //           enmienda 2026-08-29) + §10.8 (digitales — INV-MKT-14).
  // ──────────────────────────────────────────────────────────────────
  /**
   * POST /api/market/orders/:orderNumber/download — buyer downloads a
   * digital asset. Returns a signed S3-compatible URL (Q6 — 15min TTL)
   * + a JWT token (Q7 — 15min TTL) for audit.
   *
   * Behavior (Task B2 wiring + Task B1 escrow logic):
   *   - First download: sets `downloadProof.{downloadedAt,
   *     downloadToken, ip, downloadCount: 1}` + atomically releases
   *     the escrow (CAS `escrowStatus: 'holding' → 'released'`,
   *     `status: 'delivered' → 'completed'`) with the standard 30/2/68
   *     split on 100% grandTotal (INV-MKT-14 immediate release).
   *   - Subsequent downloads: increments `downloadProof.downloadCount`
   *     + returns a fresh signed URL. NO re-release (CAS guard on
   *     `escrowStatus='released'`). The per-product
   *     `digitalConfig.downloadLimit` (null = unlimited) is enforced
   *     server-side — 403 `DOWNLOAD_LIMIT_EXCEEDED` when exceeded.
   *
   * Authz: `requireAuth` + self only (backend verifies
   * `order.buyerId === req.user!.sub`). NO 2FA gate. NO rate limiter.
   *
   * Spec ref: SPEC-081 Fase 5 §5.1 (download endpoint), §4.2 (digital
   *           flow — download + immediate release); SPEC-NEGOCIO
   *           §10.8 INV-MKT-14.
   */
  async download(
    orderNumber: string,
    signal?: AbortSignal,
  ): Promise<{ downloadUrl: string; token: string }> {
    return apiFetch<{ downloadUrl: string; token: string }>(
      `/market/orders/${encodeURIComponent(orderNumber)}/download`,
      { method: 'POST', signal },
    );
  },
  /**
   * POST /api/market/orders/:orderNumber/cancel — buyer cancels a
   * service order. Only valid when `productType='service'` AND
   * `status ∈ ['pending_seller', 'confirmed']` (post-delivered NOT
   * cancellable).
   *
   * Backend behavior (Q1 ENMIENDA 2026-08-29 — service cancellation
   * 30/20 split):
   *   - `free_48h` policy + now < startDate - 48h → 100% refund of the
   *     50% deposit. `status='cancelled'`, `escrowStatus='refunded'`.
   *   - `free_48h` policy + now >= startDate - 48h → buyer loses 100%
   *     of the deposit. Split 30% grandTotal → seller + 20%
   *     grandTotal → platform. `escrowStatus='released'` (NOT
   *     refunded — partial release with 30/20 split). 0% refund.
   *   - `no_refund` policy → 0% refund regardless of timing (same
   *     30/20 split as `free_48h` post-48h).
   *
   * Returns the new `status` (always `'cancelled'`) + `refundCredits`
   * (the credit amount returned to the buyer's wallet — 0 for
   * post-48h or `no_refund`, full deposit for pre-48h `free_48h`).
   *
   * Authz: `requireAuth` + `orderCancelLimiter` (5/min/user) +
   * `requireRecent2FA` (5min 2FA — financial-grade refund OR
   * retention) + self only. The 5-min 2FA Redis key is set by the
   * `TwoFactorFinancialDialog` (TOTP) — the caller MUST open the
   * dialog before calling this method (or handle 403
   * `2FA_REQUIRED` by re-opening it).
   *
   * Spec ref: SPEC-081 Fase 5 §5.1 (cancel endpoint), §4.1 step 6
   *           (service cancellation flow), §8 (Q1 ENMIENDA 2026-08-29
   *           — 30/20 split); SPEC-NEGOCIO §10.7 step 5 ENMIENDA.
   */
  async cancelService(
    orderNumber: string,
    reason?: string,
    signal?: AbortSignal,
  ): Promise<{ status: string; refundCredits: number }> {
    return apiFetch<{ status: string; refundCredits: number }>(
      `/market/orders/${encodeURIComponent(orderNumber)}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
        signal,
      },
    );
  },

  /**
   * PR #1433 — buyer↔seller chat per order (estilo MercadoLibre / Binance P2P).
   *
   * PR #1438 (v124.2 — follow-up #A1 + #A2): added `uploadFile` (multipart
   * → 15min signed URL — step 1 of the 2-step upload-then-send flow) +
   * `getUnreadCount` (for the sidebar badge — polls every 30s + WS listener
   * for `chat:message:new`). The `send` method now accepts the 4 new
   * file-attachment fields (text is now optional — at least one of
   * text/sticker/fileUrl must be present, enforced by the backend Zod).
   *
   * Three+two methods mirroring the 5 backend endpoints. The same endpoints
   * work for BOTH buyer and seller — the backend resolves the caller's
   * role (buyer/seller/admin) via JWT + Seller.userId lookup. The frontend
   * uses the `/market/orders/...` path for both (the seller side also
   * mounts at `/seller/orders/...` but the buyer path works for sellers
   * too since the controller does buyer-OR-seller ownership resolution).
   *
   * Founder directive 2026-10-18: "que cuando se realice una compra, el
   * comprador tenga la posibilidad de abrir chat donde estén de
   * comunicación con el vendedor para que también sirva de soporte en
   * caso de disputa".
   */
  chat: {
    /**
     * GET /api/market/orders/:orderNumber/chat — list messages (paginated,
     * oldest-first). Returns the full thread context + unreadCount +
     * threadClosed + viewerRole.
     */
    async list(
      orderNumber: string,
      opts?: { page?: number; limit?: number },
      signal?: AbortSignal,
    ): Promise<import('@profileia/shared').OrderChatListResponse> {
      const params = new URLSearchParams();
      if (opts?.page) params.set('page', String(opts.page));
      if (opts?.limit) params.set('limit', String(opts.limit));
      const qs = params.toString() ? `?${params.toString()}` : '';
      return apiFetch<import('@profileia/shared').OrderChatListResponse>(
        `/market/orders/${encodeURIComponent(orderNumber)}/chat${qs}`,
        { method: 'GET', signal },
      );
    },

    /**
     * POST /api/market/orders/:orderNumber/chat — send a message. Rate-
     * limited to 30/min/user at the route layer (orderChatLimiter).
     *
     * Body (at least one of `text`/`sticker`/`fileUrl` must be present —
     * enforced by the backend Zod `.refine()`):
     *   - `text` ≤ 2000 chars (mirror DisputeMessage.text maxlength).
     *   - `sticker` ≤ 50 chars (emoji or short code).
     *   - `fileUrl` + `fileName` + `fileType` — produced by `uploadFile()`
     *     (step 1) and passed here as step 2. The client never invents
     *     URLs — only URLs the backend itself issued in the last 15min
     *     (TTL of the signed URL) are accepted (anti-SSRF).
     *
     * Returns the created message (the frontend can optimistically append
     * it to the local list without a full refetch).
     */
    async send(
      orderNumber: string,
      body: import('@profileia/shared').OrderChatMessageInput,
      signal?: AbortSignal,
    ): Promise<import('@profileia/shared').OrderChatSendResponse> {
      return apiFetch<import('@profileia/shared').OrderChatSendResponse>(
        `/market/orders/${encodeURIComponent(orderNumber)}/chat`,
        { method: 'POST', body: JSON.stringify(body), signal },
      );
    },

    /**
     * POST /api/market/orders/:orderNumber/chat/read — mark all unread
     * messages in this thread (where the caller is the recipient) as read.
     *
     * Returns the count of messages that were transitioned from
     * `readAt: null` → `readAt: <now>`.
     */
    async markRead(
      orderNumber: string,
      signal?: AbortSignal,
    ): Promise<import('@profileia/shared').OrderChatMarkReadResponse> {
      return apiFetch<import('@profileia/shared').OrderChatMarkReadResponse>(
        `/market/orders/${encodeURIComponent(orderNumber)}/chat/read`,
        { method: 'POST', signal },
      );
    },

    /**
     * POST /api/market/orders/:orderNumber/chat/file — upload a file to
     * MinIO/S3 and return a 15min signed URL + metadata (step 1 of the
     * 2-step upload-then-send flow). The client passes the returned
     * `{ fileUrl, fileName, fileType }` to `send()` to associate the file
     * with a message.
     *
     * Multipart form-data with a single `file` field (mirror
     * `api.directMessages.uploadFile`). The backend validates MIME against
     * the DirectMessage whitelist + size ≤ 15 MB (defense-in-depth vs the
     * multer fileFilter).
     *
     * Rate-limited to 10 uploads/min/user at the route layer
     * (orderChatFileLimiter — 1/3 of the text-message cap because files
     * are larger + rarer in normal chat use).
     *
     * Anti-P-263: the internal S3 `s3Key` is NEVER returned to the client
     * — only the signed URL.
     *
     * Anti-P-043: AbortController for in-flight upload cancellation on
     * unmount (the parent component passes `signal`).
     */
    async uploadFile(
      orderNumber: string,
      file: File,
      signal?: AbortSignal,
    ): Promise<import('@profileia/shared').UploadedChatFileDTO> {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<import('@profileia/shared').UploadedChatFileDTO>(
        `/market/orders/${encodeURIComponent(orderNumber)}/chat/file`,
        {
          method: 'POST',
          body: formData,
          skipJsonStringify: true,
          signal,
        },
      );
    },

    /**
     * GET /api/market/orders/chat/unread-count — count unread messages
     * addressed to the caller across ALL their order chats (where the
     * caller is buyer OR seller). Admins return 0 (admins use
     * DisputeMessage, not this sidebar).
     *
     * Used by the `OrderChatBadge` sidebar component (mirror
     * `DirectMessageBadge` — polls every 30s + WS listener for
     * `chat:message:new`).
     */
    async getUnreadCount(
      signal?: AbortSignal,
    ): Promise<import('@profileia/shared').OrderChatUnreadCountResponse> {
      return apiFetch<import('@profileia/shared').OrderChatUnreadCountResponse>(
        '/market/orders/chat/unread-count',
        { signal },
      );
    },
  },
};
